import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { LogOut, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import SidebarNav from './SidebarNav';
import { BrandMark, Button, Sheet, SheetContent, SheetTitle, Tooltip, TooltipProvider, useAtLeast, cn } from '@racing/ui';

/**
 * Header height. Tall enough for the official logo (150px wide, ~46px tall)
 * plus its required clear space above and below.
 */
const HEADER_H = 'h-[72px]';

const RAIL_WIDTH = 248;
const RAIL_WIDTH_COLLAPSED = 68;

/** ADMINISTRATOR -> "Administrator", TRACK_OPERATOR -> "Track Operator". */
const formatRole = (role: string) =>
  role
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const AppLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  /**
   * The breakpoint that decides the whole layout. At `md` and up the rail is
   * permanent beside the content; below it the navigation becomes an overlay
   * sheet so data tables get the full width — a portrait tablet cannot spare
   * 248px.
   */
  const isDesktop = useAtLeast('md');
  /** Below `sm` the product name would crowd the header actions off the bar. */
  const hasRoomForProductName = useAtLeast('sm');

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the overlay on navigation, or it covers the page just requested.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const railWidth = collapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH;
  const primaryRole = user?.roles?.[0];

  return (
    <TooltipProvider delayDuration={300}>
      <div className="min-h-dvh bg-canvas">
        <header
          className={cn(
            'fixed inset-x-0 top-0 z-40 flex items-center gap-2 px-3 sm:px-4',
            HEADER_H,
            'border-b border-white/10 text-white',
            // The #5f0397 -> #230a33 gradient the brand guidelines specify.
            'bg-[linear-gradient(160deg,#5f0397_0%,#230a33_100%)]'
          )}
        >
          {!isDesktop && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open navigation menu"
              className="text-white hover:bg-white/15"
              onClick={() => setMobileOpen(true)}
            >
              <Menu />
            </Button>
          )}

          <div className="min-w-0 flex-1">
            <BrandMark showProductName={hasRoomForProductName} />
          </div>

          {user && (
            <div className="hidden min-w-0 flex-col items-end sm:flex">
              <span className="truncate font-sans text-sm font-semibold leading-tight">
                {user.username}
              </span>
              {primaryRole && (
                <span className="truncate font-sans text-xs leading-tight text-white/70">
                  {formatRole(primaryRole)}
                </span>
              )}
            </div>
          )}

          <span aria-hidden className="mx-1 hidden h-8 w-px bg-white/25 sm:block" />

          <Tooltip label="Sign out">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Sign out"
              className="text-white hover:bg-white/15"
              onClick={logout}
            >
              <LogOut />
            </Button>
          </Tooltip>
        </header>

        {/* Desktop rail */}
        {isDesktop && (
          <aside
            className={cn(
              'fixed bottom-0 left-0 z-30 flex flex-col border-r border-line bg-surface transition-[width] duration-200',
              'top-[72px]'
            )}
            style={{ width: railWidth }}
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              <SidebarNav collapsed={collapsed} />
            </div>
            <div
              className={cn(
                'flex border-t border-line p-1.5',
                collapsed ? 'justify-center' : 'justify-end'
              )}
            >
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                onClick={() => setCollapsed((value) => !value)}
              >
                {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
              </Button>
            </div>
          </aside>
        )}

        {/* Mobile / tablet overlay */}
        {!isDesktop && (
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent>
              <SheetTitle className="flex h-[72px] shrink-0 items-center border-b border-line px-4 font-sans text-base font-bold text-ink">
                Navigation
              </SheetTitle>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <SidebarNav onNavigate={() => setMobileOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        )}

        <main
          className={cn(
            // `min-w-0` is what stops a wide table pushing the page sideways
            // instead of scrolling inside its own container.
            'min-w-0 px-4 pb-10 pt-[calc(72px+1rem)] sm:px-6 sm:pb-6 sm:pt-[calc(72px+1.5rem)]',
            'transition-[padding] duration-200'
          )}
          style={isDesktop ? { paddingLeft: railWidth + 24 } : undefined}
        >
          <Outlet />
        </main>
      </div>
    </TooltipProvider>
  );
};

export default AppLayout;
